import React, { useState } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCheck } from "@fortawesome/free-solid-svg-icons";
import {
  Modal,
  ModalBackground,
  ModalClose,
  ModalContent,
  ModalHeading,
} from "../library/Modal";
import Engagement from "../../apiResources/Engagement";
import CurrentUser from "../../apiResources/CurrentUser";
import {
  Row,
  Top,
  ColumnTitle,
  Bottom,
  ColumnData,
  Table,
  TableRow,
  TableColumn,
} from "./styles/engagement-info-styles";

type TabState = "status" | "details";

const EngagementInfoModal = (props: Props) => {
  const { engagement, user, onDismiss } = props;
  const { job } = engagement;

  const [activeTab, setActiveTab] = useState<TabState>("status");

  return (
    <Modal isOpen onDismiss={onDismiss} id="engagement-info-modal">
      <ModalBackground onClick={onDismiss} />
      <ModalContent>
        <ModalHeading>{job.title}</ModalHeading>

        <TabGroup>
          <TabButton
            active={activeTab === "status"}
            type="button"
            onClick={() => setActiveTab("status")}
          >
            Job Status
          </TabButton>
          <TabButton
            active={activeTab === "details"}
            type="button"
            onClick={() => setActiveTab("details")}
          >
            Job Details
          </TabButton>
        </TabGroup>

        {activeTab === "status" && (
          <>
            <Row>
              <Top>
                <ColumnTitle>Submitted CVs</ColumnTitle>
                <ColumnTitle>CVs Read</ColumnTitle>
                <ColumnTitle>Offers Made</ColumnTitle>
              </Top>
              <Bottom>
                <ColumnData data-test-id="job-app-count">
                  {job.applicationsCount}
                </ColumnData>
                <ColumnData data-test-id="app-read-count">
                  {job.readApplicationsCount}
                </ColumnData>
                <ColumnData data-test-id="offers-made">0</ColumnData>
              </Bottom>
            </Row>
            <Row>
              <Top>
                <ColumnTitle>Phone Interviews</ColumnTitle>
                <ColumnTitle>1st Stage Interviews</ColumnTitle>
                <ColumnTitle>2nd Stage Interviews</ColumnTitle>
              </Top>
              <Bottom>
                <ColumnData data-test-id="phone-count">
                  {job.phoneInterviews}
                </ColumnData>
                <ColumnData data-test-id="first-stage-count">
                  {job.firstStageInterviews}
                </ColumnData>
                <ColumnData data-test-id="second-stage-count">
                  {job.secondStageInterviews}
                </ColumnData>
              </Bottom>
            </Row>
            <Row>
              <Top>
                <ColumnTitle>Candidates Rejected</ColumnTitle>
                <ColumnTitle>Competencies</ColumnTitle>
              </Top>
              <Bottom>
                <ColumnData data-test-id="apps-rejected">
                  {job.candidatesRejected}
                </ColumnData>
                <ColumnData data-test-id="competency-stage">
                  {job.competencyStageCount}
                </ColumnData>
              </Bottom>
            </Row>
            <Row>
              <Top>
                <ColumnTitle>Placements Made</ColumnTitle>
                <ColumnTitle>Emp. Avg. Response</ColumnTitle>
              </Top>
              <Bottom>
                <ColumnData data-test-id="placements-made">
                  {job.placements.length}
                </ColumnData>
                <ColumnData data-test-id="emp-avg-response">
                  {job.employer.averageTimeToRespond || "new employer"}
                </ColumnData>
              </Bottom>
            </Row>
          </>
        )}

        {activeTab === "details" && (
          <Table>
            {!user.isPortalUser && (
              <TableRow>
                <TableColumn bold>Your fee:</TableColumn>
                <TableColumn data-test-id="job-fee">
                  {job.placementFeePercent}%
                </TableColumn>
              </TableRow>
            )}
            <TableRow>
              <TableColumn bold>Job sector:</TableColumn>
              <TableColumn>{job.sector.name}</TableColumn>
            </TableRow>
            <TableRow>
              <TableColumn bold>Location:</TableColumn>
              <TableColumn>{job.location}</TableColumn>
            </TableRow>
            <TableRow>
              <TableColumn bold>Salary:</TableColumn>
              <TableColumn data-test-id="job-salary">
                <strong>{job.currency.symbol}</strong> {job.salary}
              </TableColumn>
            </TableRow>
            <TableRow>
              <TableColumn bold>Job type:</TableColumn>
              <TableColumn>{job.jobType}</TableColumn>
            </TableRow>
            {job.numberOfPositions > 1 && (
              <TableRow>
                <TableColumn bold>Number of Positions:</TableColumn>
                <TableColumn>{job.numberOfPositions}</TableColumn>
              </TableRow>
            )}
            <TableRow>
              <TableColumn bold>Exclusive job:</TableColumn>
              <TableColumn>
                {job.exclusive ? (
                  <FontAwesomeIcon icon={faCheck} />
                ) : (
                  <FontAwesomeIcon icon={faTimes} />
                )}
              </TableColumn>
            </TableRow>
            <TableRow>
              <TableColumn bold>Account manager:</TableColumn>
              <TableColumn>{job.employer.accountManager}</TableColumn>
            </TableRow>
            <TableRow>
              <TableColumn bold>Reason for vacancy:</TableColumn>
              <TableColumn>{job.vacancyReason}</TableColumn>
            </TableRow>
            <TableRow>
              <TableColumn bold>Must have a degree:</TableColumn>
              <TableColumn>
                {job.degreeRequired ? (
                  <FontAwesomeIcon icon={faCheck} />
                ) : (
                  <FontAwesomeIcon icon={faTimes} />
                )}
              </TableColumn>
            </TableRow>
            <TableRow>
              <TableColumn bold>Consider VISA sponsorship:</TableColumn>
              <TableColumn>
                {job.visaSponsorship ? (
                  <FontAwesomeIcon icon={faCheck} />
                ) : (
                  <FontAwesomeIcon icon={faTimes} />
                )}
              </TableColumn>
            </TableRow>
            <TableRow>
              <TableColumn bold>Consider relocating from EU:</TableColumn>
              <TableColumn>
                {job.relocationAssistance ? (
                  <FontAwesomeIcon icon={faCheck} />
                ) : (
                  <FontAwesomeIcon icon={faTimes} />
                )}
              </TableColumn>
            </TableRow>
            <TableRow>
              <TableColumn bold>Stages of interview process:</TableColumn>
              <TableColumn data-test-id="interview-stages">
                {job.interviewStages}
              </TableColumn>
            </TableRow>
            <TableRow>
              <TableColumn bold>Time for CV feedback:</TableColumn>
              <TableColumn>{job.feedbackTimescale.description}</TableColumn>
            </TableRow>
            <TableRow>
              <TableColumn bold>Keywords:</TableColumn>
              <TableColumn>{job.keywords}</TableColumn>
            </TableRow>
            <TableRow>
              <TableColumn bold>Custom terms:</TableColumn>
              <TableColumn>
                {job.employer.customTerms
                  ? job.employer.customTerms.replace(/<[^>]+>/g, "")
                  : ""}
              </TableColumn>
            </TableRow>
          </Table>
        )}

        <ModalClose aria-label="Close modal" onClick={onDismiss} />
      </ModalContent>
    </Modal>
  );
};

const TabGroup = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const TabButton = styled.button<{ active: boolean }>`
  appearance: none;
  background: none;
  border: none;
  border-bottom-width: 3px;
  border-bottom-style: solid;
  border-bottom-color: ${({ active, theme }) =>
    active ? theme.colors.secondary : "transparent"};
  font-size: 18px;
  font-weight: 700;
  padding: 0;
  margin-right: 1rem;
  margin-left: 1rem;
`;

type Props = {
  onDismiss: () => void;
  engagement: Engagement;
  user: CurrentUser;
};

export default EngagementInfoModal;
